import type { WebsiteHitRow } from 'aevocam-rest-api-spec'
import {
  estimatedDailyAverage,
  estimatedHourlyAverage,
  startOfDayInTimeZone,
  startOfHourInTimeZone,
  TELEMETRY_TIME_ZONE,
} from '../telemetry/telemetryHelpers'

export const WEB_HIT_TIME_ZONE = TELEMETRY_TIME_ZONE
export const WEB_HIT_HOURLY_PERIOD_SEC = 3600
export const WEB_HIT_DAILY_PERIOD_SEC = 86400
export const WEB_HIT_HOURLY_BUCKET_COUNT = 3 * 24
export const WEB_HIT_DAILY_BUCKET_COUNT = 90
export const WEB_HIT_REFRESH_MS = 10_000

const HOUR_MS = WEB_HIT_HOURLY_PERIOD_SEC * 1000
const DAY_MS = WEB_HIT_DAILY_PERIOD_SEC * 1000

export type WebHitCard = {
  key: string
  label: string
  bucketFrom: Date
  buckets: number[]
  hourlyAverage: number
  hourlyDurationAvgMs?: number
  hourlyDurationMaxMs?: number
  dailyBucketFrom: Date
  dailyBuckets: number[]
  dailyAverage: number
  dailyDurationAvgMs?: number
  dailyDurationMaxMs?: number
}

export function formatDurationMs(ms: number | undefined): string | undefined {
  if (ms == null) return undefined
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} s`
}

export function formatWebHitLabel(path: string): string {
  return path || '/'
}

export function webHitHourlyWindow(now = new Date()) {
  const currentHourStart = startOfHourInTimeZone(now, WEB_HIT_TIME_ZONE)
  const from = new Date(
    currentHourStart.getTime() - (WEB_HIT_HOURLY_BUCKET_COUNT - 1) * HOUR_MS,
  )
  const to = new Date(currentHourStart.getTime() + HOUR_MS)
  return { from, to, now }
}

export function webHitDailyWindow(now = new Date()) {
  const currentDayStart = startOfDayInTimeZone(now, WEB_HIT_TIME_ZONE)
  const from = new Date(
    currentDayStart.getTime() - (WEB_HIT_DAILY_BUCKET_COUNT - 1) * DAY_MS,
  )
  const to = new Date(currentDayStart.getTime() + DAY_MS)
  return { from, to, now }
}

export function buildWebHitTimeSeries(
  rows: WebsiteHitRow[],
  from: Date,
  to: Date,
  periodSec: number,
): number[] {
  const periodMs = periodSec * 1000
  const slotCount = Math.ceil((to.getTime() - from.getTime()) / periodMs)
  const buckets = new Array<number>(slotCount).fill(0)
  const fromMs = from.getTime()

  for (const row of rows) {
    if (row.createdAt == null || row.count == null) continue
    const idx = Math.floor((new Date(row.createdAt).getTime() - fromMs) / periodMs)
    if (idx >= 0 && idx < slotCount) {
      buckets[idx] = (buckets[idx] ?? 0) + row.count
    }
  }

  return buckets
}

function groupRowsByPath(rows: WebsiteHitRow[]): Map<string, WebsiteHitRow[]> {
  const grouped = new Map<string, WebsiteHitRow[]>()
  for (const row of rows) {
    const path = row.path ?? ''
    const group = grouped.get(path) ?? []
    group.push(row)
    grouped.set(path, group)
  }
  return grouped
}

type DurationStats = {
  avg?: number
  max?: number
}

function durationStatsByPath(rows: WebsiteHitRow[]): Map<string, DurationStats> {
  const map = new Map<string, DurationStats>()
  for (const row of rows) {
    const path = row.path ?? ''
    map.set(path, {
      avg: row.durationAvg,
      max: row.durationMax,
    })
  }
  return map
}

export function rowsToWebHitCards(
  hourlyRows: WebsiteHitRow[],
  dailyRows: WebsiteHitRow[],
  hourlyDurationRows: WebsiteHitRow[],
  dailyDurationRows: WebsiteHitRow[],
  hourlyFrom: Date,
  hourlyTo: Date,
  dailyFrom: Date,
  dailyTo: Date,
  now: Date,
): WebHitCard[] {
  const hourlyByPath = groupRowsByPath(hourlyRows)
  const dailyByPath = groupRowsByPath(dailyRows)
  const hourlyDurationByPath = durationStatsByPath(hourlyDurationRows)
  const dailyDurationByPath = durationStatsByPath(dailyDurationRows)
  const paths = [...new Set([
    ...hourlyByPath.keys(),
    ...dailyByPath.keys(),
    ...hourlyDurationByPath.keys(),
    ...dailyDurationByPath.keys(),
  ])]

  return paths.map(path => {
    const hourlyDuration = hourlyDurationByPath.get(path)
    const dailyDuration = dailyDurationByPath.get(path)
    const buckets = buildWebHitTimeSeries(
      hourlyByPath.get(path) ?? [],
      hourlyFrom,
      hourlyTo,
      WEB_HIT_HOURLY_PERIOD_SEC,
    )
    const dailyBuckets = buildWebHitTimeSeries(
      dailyByPath.get(path) ?? [],
      dailyFrom,
      dailyTo,
      WEB_HIT_DAILY_PERIOD_SEC,
    )

    return {
      key: path,
      label: formatWebHitLabel(path),
      bucketFrom: hourlyFrom,
      buckets,
      hourlyAverage: estimatedHourlyAverage(buckets, now),
      hourlyDurationAvgMs: hourlyDuration?.avg,
      hourlyDurationMaxMs: hourlyDuration?.max,
      dailyBucketFrom: dailyFrom,
      dailyBuckets,
      dailyAverage: estimatedDailyAverage(dailyBuckets, now),
      dailyDurationAvgMs: dailyDuration?.avg,
      dailyDurationMaxMs: dailyDuration?.max,
    }
  }).sort((a, b) =>
    a.label.localeCompare(b.label),
  )
}
